import React, { ButtonHTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { Link, LinkProps } from 'react-router-dom';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'outline-white' | 'text' | 'green' | 'danger';
type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonBaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

interface ButtonAsButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonBaseProps {
  to?: never;
}

interface ButtonAsLinkProps extends ButtonBaseProps, Omit<LinkProps, 'to'> {
  to: string;
}

type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

const getButtonStyles = (variant: ButtonVariant) => {
  switch (variant) {
    case 'primary':
      return css`
        background-color: transparent;
        color: white;
        border: 1px solid white;
        
        &:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
      `;
    case 'secondary':
      return css`
        background-color: transparent;
        color: var(--text-primary);
        border: 1px solid var(--text-primary);
        
        &:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
      `;
    case 'outline':
      return css`
        background-color: transparent;
        color: var(--accent-primary);
        border: 1px solid var(--accent-primary);
        
        &:hover {
          background-color: rgba(13, 148, 136, 0.1);
        }
      `;
    case 'outline-white':
      return css`
        background-color: transparent;
        color: white;
        border: 1px solid white;
        
        &:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
      `;
    case 'text':
      return css`
        background-color: transparent;
        color: var(--accent-primary);
        border: none;
        padding: 0;
        
        &:hover {
          text-decoration: underline;
        }
      `;
    case 'green':
      return css`
        background-color: transparent;
        color: #0d9488;
        border: 2px solid #0d9488;
        
        &:hover {
          background-color: rgba(13, 148, 136, 0.1);
          transform: translateY(-3px);
          box-shadow: 0 5px 15px rgba(13, 148, 136, 0.2);
        }
      `;
    case 'danger':
      return css`
        background-color: transparent;
        color: #ef4444;
        border: 2px solid #ef4444;
        
        &:hover {
          background-color: rgba(239, 68, 68, 0.1);
          transform: translateY(-3px);
          box-shadow: 0 5px 15px rgba(239, 68, 68, 0.2);
        }
      `;
    default:
      return css`
        background-color: transparent;
        color: white;
        border: 1px solid white;
      `;
  }
};

const getButtonSize = (size: ButtonSize) => {
  switch (size) {
    case 'small':
      return css`
        padding: 0.5rem 1rem;
        font-size: 0.8rem;
      `;
    case 'medium':
      return css`
        padding: 0.75rem 1.5rem;
        font-size: 0.9rem;
      `;
    case 'large':
      return css`
        padding: 1rem 2rem;
        font-size: 1rem;
      `;
    default:
      return '';
  }
};

interface StyledButtonProps {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
  $hasIcon: boolean;
  $iconPosition: 'left' | 'right';
}

const buttonStyles = css<StyledButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 0;
  font-weight: 500;
  letter-spacing: 0.05rem;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  flex-direction: ${({ $iconPosition }) => 
    $iconPosition === 'right' ? 'row-reverse' : 'row'};
  
  ${({ $variant }) => getButtonStyles($variant)};
  ${({ $size }) => getButtonSize($size)};
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StyledButton = styled.button<StyledButtonProps>`
  ${buttonStyles}
`;

const StyledLink = styled(Link)<StyledButtonProps>`
  ${buttonStyles}
`;

const Button = (props: ButtonProps) => {
  const {
    variant = 'primary',
    size = 'medium',
    fullWidth = false,
    icon,
    iconPosition = 'left',
    children,
    ...rest
  } = props;
  
  const commonProps = {
    $variant: variant,
    $size: size,
    $fullWidth: fullWidth,
    $hasIcon: !!icon,
    $iconPosition: iconPosition,
  };
  
  if ('to' in props && props.to) {
    return (
      <StyledLink
        {...commonProps}
        {...rest as LinkProps}
        to={props.to}
      >
        {icon}
        {children}
      </StyledLink>
    );
  }
  
  return (
    <StyledButton
      {...commonProps}
      {...rest as ButtonHTMLAttributes<HTMLButtonElement>}
    >
      {icon}
      {children}
    </StyledButton>
  );
};

export default Button; 